import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { getAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { NotesIcon } from '../../components/notes/NotesIcon';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  TooltipProvider, Tooltip, TooltipTrigger, TooltipContent,
} from '../../components/ui/tooltip';
import {
  Download, Loader2, X, FileText, Home,
  Search, ChevronsDown, Eye, ArrowUpDown, TrendingDown, TrendingUp,
  DollarSign, Receipt, Info,
} from 'lucide-react';

const PAGE_SIZE = 50;

const fmtC = (n) =>
  n == null ? '$0.00' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const methodLabel = (m) => {
  if (!m) return '\u2014';
  const lower = m.toLowerCase();
  if (lower === 'ach') return 'ACH';
  if (lower === 'eft') return 'EFT';
  if (lower === 'credit' || lower === 'credit_card' || lower === 'cc') return 'Credit Card';
  if (lower === 'cheque' || lower === 'check') return 'Cheque';
  return m;
};

export default function VarianceReport() {
  const [currentAccount, setCurrentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  useEffect(() => {
    const check = () => {
      const s = localStorage.getItem('current_account');
      const acc = s ? JSON.parse(s) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) setCurrentAccount(acc);
    };
    const h = setInterval(check, 800);
    window.addEventListener('storage', check);
    return () => { clearInterval(h); window.removeEventListener('storage', check); };
  }, [currentAccount]);
  const accountId = currentAccount?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const filterProperty = searchParams.get('property_id') || '';
  const filterEIN = searchParams.get('ein') || '';
  const filterAccount = searchParams.get('account_number') || '';
  const filterVendor = searchParams.get('vendor') || '';
  const filterServiceAddr = searchParams.get('service_address') || '';
  const filterMatch = searchParams.get('match_filter') || 'mismatch';

  const [einInput, setEinInput] = useState(filterEIN);
  const [accountInput, setAccountInput] = useState(filterAccount);
  const [vendorInput, setVendorInput] = useState(filterVendor);
  const [serviceInput, setServiceInput] = useState(filterServiceAddr);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    total: 0, has_more: false,
    total_approved: 0, total_paid: 0, total_variance: 0, total_service_fee: 0,
    total_paid_bp: 0, total_paid_anchor: 0, total_paid_other: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);

  const [filterOptions, setFilterOptions] = useState({ properties: [], vendors: [], payment_methods: [] });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerPdf, setDrawerPdf] = useState(null);
  const [drawerPdfUrl, setDrawerPdfUrl] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  useEffect(() => {
    const t = setTimeout(() => setParam('ein', einInput), 350);
    return () => clearTimeout(t);
  }, [einInput]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => setParam('account_number', accountInput), 350);
    return () => clearTimeout(t);
  }, [accountInput]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => setParam('vendor', vendorInput), 350);
    return () => clearTimeout(t);
  }, [vendorInput]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => setParam('service_address', serviceInput), 350);
    return () => clearTimeout(t);
  }, [serviceInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildParams = useCallback((page) => {
    const p = { account_id: accountId, page, limit: PAGE_SIZE };
    if (filterProperty) p.property_id = filterProperty;
    if (filterEIN) p.ein = filterEIN;
    if (filterAccount) p.account_number = filterAccount;
    if (filterVendor) p.vendor = filterVendor;
    if (filterServiceAddr) p.service_address = filterServiceAddr;
    p.match_filter = filterMatch || 'mismatch';
    return p;
  }, [accountId, filterProperty, filterEIN, filterAccount, filterVendor, filterServiceAddr, filterMatch]);

  const loadFirstPage = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    pageRef.current = 1;
    try {
      const { data } = await api.get('/payments/v2/variance/report', {
        params: buildParams(1),
      });
      setRows(data.rows || []);
      setMeta({
        total: data.total || 0,
        has_more: data.has_more,
        total_approved: data.total_approved || 0,
        total_paid: data.total_paid || 0,
        total_variance: data.total_variance || 0,
        total_service_fee: data.total_service_fee || 0,
        total_paid_bp: data.total_paid_bp || 0,
        total_paid_anchor: data.total_paid_anchor || 0,
        total_paid_other: data.total_paid_other || 0,
      });
    } catch (err) {
      toast.error('Failed to load Variance report');
    } finally {
      setLoading(false);
    }
  }, [accountId, buildParams]);

  const loadMore = useCallback(async () => {
    if (!accountId || loadingMore || !meta.has_more) return;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const { data } = await api.get('/payments/v2/variance/report', {
        params: buildParams(next),
      });
      setRows(prev => [...prev, ...(data.rows || [])]);
      setMeta(m => ({ ...m, has_more: data.has_more, total: data.total }));
      pageRef.current = next;
    } catch (err) { /* silent */ }
    finally { setLoadingMore(false); }
  }, [accountId, buildParams, loadingMore, meta.has_more]);

  const loadFilterOptions = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get('/payments/v2/variance/report/filter-options', {
        params: { account_id: accountId },
      });
      setFilterOptions(data || {});
    } catch { /* silent */ }
  }, [accountId]);

  useEffect(() => { loadFilterOptions(); }, [loadFilterOptions]);
  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);

  const hasActiveFilters = filterProperty || filterEIN || filterAccount || filterVendor || filterServiceAddr || (filterMatch && filterMatch !== 'mismatch');
  const clearFilters = () => {
    setEinInput(''); setAccountInput(''); setVendorInput(''); setServiceInput('');
    const next = new URLSearchParams();
    next.set('match_filter', 'mismatch');
    setSearchParams(next);
  };

  const downloadCsv = async () => {
    const toastId = toast.loading('Preparing CSV export\u2026');
    try {
      const params = { ...buildParams(1) };
      delete params.page; delete params.limit;
      const qs = new URLSearchParams(
        Object.entries(params).reduce((acc, [k, v]) => { if (v !== undefined && v !== '') acc[k] = v; return acc; }, {})
      ).toString();
      const auth = getAuth();
      const token = auth?.token;
      const url = `${api.defaults.baseURL}/payments/v2/variance/report/export?${qs}`;
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok || !resp.body) { toast.dismiss(toastId); toast.error('CSV export failed'); return; }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      const chunks = [];
      let rowsCount = 0; let last = Date.now(); let partial = '';
      while (true) { // eslint-disable-line no-constant-condition
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        const text = decoder.decode(value, { stream: true });
        partial += text;
        const nc = (partial.match(/\n/g) || []).length;
        partial = partial.slice(partial.lastIndexOf('\n') + 1);
        rowsCount += nc;
        if (Date.now() - last > 200) {
          toast.loading(`Generating CSV\u2026 ${Math.max(0, rowsCount - 1).toLocaleString()} rows streamed`, { id: toastId });
          last = Date.now();
        }
      }
      const blob = new Blob(chunks, { type: 'text/csv' });
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `variance_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      const finalRows = Math.max(0, rowsCount - 1);
      toast.success(`CSV downloaded \u00b7 ${finalRows.toLocaleString()} rows`, { id: toastId });
    } catch {
      toast.dismiss(toastId); toast.error('CSV export failed');
    }
  };

  const openInvoice = async (row) => {
    setDrawerRow(row);
    setDrawerPdf(null);
    setDrawerPdfUrl(null);
    setDrawerLoading(true);
    setDrawerOpen(true);
    try {
      // Direct endpoint: /payments/v2/bill-pdf/{bill_id} returns either
      // base64_data (BillPortal proxy) or pdf_url (S3 presigned) — same
      // contract used by Bills page, PFD, and Workspace.
      const { data } = await api.get(
        `/payments/v2/bill-pdf/${row.bill_id}`,
        { params: { account_id: accountId } }
      );
      if (data?.base64_data) setDrawerPdf(data.base64_data);
      else if (data?.pdf_url) setDrawerPdfUrl(data.pdf_url);
    } catch {
      setDrawerPdf(null);
      setDrawerPdfUrl(null);
    } finally {
      setDrawerLoading(false);
    }
  };
  const pdfDataUrl = drawerPdf
    ? `data:application/pdf;base64,${drawerPdf}`
    : (drawerPdfUrl || null);

  if (!accountId) {
    return (
      <MainLayout>
        <div className="p-8 text-center text-sm text-muted-foreground">Please select an organization.</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
        <div className="space-y-4 p-4 md:p-6" data-testid="variance-report-page">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-sm">
                <ArrowUpDown className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="vr-title">Variance Report</h1>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>Shows only transactions with variances paid by BillPortal (Approved + Paid PFs).</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex" data-testid="vr-variance-info">
                        <Info className="h-3 w-3 text-muted-foreground/70 hover:text-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[340px] text-xs p-3">
                      <p className="font-semibold mb-1">Why do variances happen?</p>
                      <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                        <li>Vendor portals may cap or restrict the exact amount that can be paid in a single transaction.</li>
                        <li>A prior incumbent or the customer may have already paid part of the bill before BillPortal took over.</li>
                        <li>The vendor may not have applied earlier payments when generating the latest bill, inflating the requested amount.</li>
                      </ul>
                      <p className="mt-2 text-[10px] italic">Use this report to reconcile and tag each variance with its root cause.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <NotesIcon pageKey="variance_report" pageLabel="Variance Report" accountId={accountId} />
            </div>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={loading} data-testid="vr-download-csv">
              <Download className="h-3.5 w-3.5 mr-1" /> Download CSV
            </Button>
          </div>

          {/* KPI Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="border rounded-lg p-4 bg-card" data-testid="vr-kpi-approved">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded bg-slate-100"><DollarSign className="h-4 w-4 text-slate-600" /></div>
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">Total Approved</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{fmtC(meta.total_approved)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{meta.total.toLocaleString()} transactions</p>
            </div>
            <div className="border rounded-lg p-4 bg-card" data-testid="vr-kpi-paid">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded bg-emerald-100"><Receipt className="h-4 w-4 text-emerald-600" /></div>
                <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Total Paid (BP)</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700">{fmtC(meta.total_paid)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">BillPortal-paid only · Approved + Paid PFs</p>
            </div>
            <div className={`border rounded-lg p-4 ${meta.total_variance > 0 ? 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200' : meta.total_variance < 0 ? 'bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200' : 'bg-card'}`} data-testid="vr-kpi-variance">
              <div className="flex items-center gap-2 mb-1">
                <div className={`p-1.5 rounded ${meta.total_variance > 0 ? 'bg-red-100' : meta.total_variance < 0 ? 'bg-blue-100' : 'bg-slate-100'}`}>
                  {meta.total_variance > 0
                    ? <TrendingDown className="h-4 w-4 text-red-600" />
                    : meta.total_variance < 0
                      ? <TrendingUp className="h-4 w-4 text-blue-600" />
                      : <ArrowUpDown className="h-4 w-4 text-slate-500" />
                  }
                </div>
                <span className={`text-xs font-medium uppercase tracking-wide ${meta.total_variance > 0 ? 'text-red-700' : meta.total_variance < 0 ? 'text-blue-700' : 'text-slate-600'}`}>
                  Total Variance
                </span>
              </div>
              <p className={`text-2xl font-bold ${meta.total_variance > 0 ? 'text-red-700' : meta.total_variance < 0 ? 'text-blue-700' : 'text-slate-700'}`}>
                {fmtC(meta.total_variance)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {meta.total_variance > 0 ? 'underpaid' : meta.total_variance < 0 ? 'overpaid' : 'exact match'}
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-card" data-testid="vr-kpi-fee">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded bg-amber-100"><DollarSign className="h-4 w-4 text-amber-600" /></div>
                <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">Total Svc Fees</span>
              </div>
              <p className="text-2xl font-bold text-amber-700">{fmtC(meta.total_service_fee)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">excluded from variance</p>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="border rounded-lg p-3 bg-card">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filterMatch} onValueChange={(v) => setParam('match_filter', v)}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="vr-filter-match">
                  <ArrowUpDown className="h-3 w-3 mr-1 opacity-60" />
                  <SelectValue placeholder="Mismatches only" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mismatch">Mismatches only</SelectItem>
                  <SelectItem value="match">Exact matches only</SelectItem>
                  <SelectItem value="all">All transactions</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterProperty} onValueChange={(v) => setParam('property_id', v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid="vr-filter-property">
                  <Home className="h-3 w-3 mr-1 opacity-60" /><SelectValue placeholder="All Properties" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All Properties</SelectItem>
                  {filterOptions.properties?.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={einInput} onChange={(e) => setEinInput(e.target.value)}
                  placeholder="EIN / tax id"
                  className="h-8 w-32 text-xs pl-6"
                  data-testid="vr-filter-ein"
                />
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={accountInput} onChange={(e) => setAccountInput(e.target.value)}
                  placeholder="Account #"
                  className="h-8 w-36 text-xs pl-6"
                  data-testid="vr-filter-account"
                />
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={vendorInput} onChange={(e) => setVendorInput(e.target.value)}
                  placeholder="Vendor"
                  className="h-8 w-36 text-xs pl-6"
                  data-testid="vr-filter-vendor"
                />
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={serviceInput} onChange={(e) => setServiceInput(e.target.value)}
                  placeholder="Service address"
                  className="h-8 w-44 text-xs pl-6"
                  data-testid="vr-filter-address"
                />
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="vr-clear-filters">
                  <X className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
              )}

              <span className="ml-auto text-xs text-muted-foreground">
                Showing <strong>{rows.length.toLocaleString()}</strong> of <strong>{meta.total.toLocaleString()}</strong>
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-md w-full overflow-x-auto">
            <Table className="w-full min-w-[1900px]" data-testid="vr-table">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="text-xs font-semibold w-[36px] px-2">#</TableHead>
                  <TableHead className="text-xs font-semibold w-[180px]">Property / Service Address</TableHead>
                  <TableHead className="text-xs font-semibold w-[110px]">Account # / PF</TableHead>
                  <TableHead className="text-xs font-semibold w-[100px]">Vendor</TableHead>
                  <TableHead className="text-xs font-semibold w-[88px] text-right px-2">Approved</TableHead>
                  <TableHead className="text-xs font-semibold w-[88px] text-right px-2">Paid</TableHead>
                  <TableHead className="text-xs font-semibold w-[70px] text-right px-2">Svc Fee</TableHead>
                  <TableHead className="text-xs font-semibold w-[80px] px-2">Pmt Type</TableHead>
                  <TableHead className="text-xs font-bold w-[96px] text-right px-2 bg-sky-50 text-sky-900" data-testid="vr-col-variance">
                    <span className="inline-flex items-center gap-1"><ArrowUpDown className="h-3 w-3" /> Variance</span>
                  </TableHead>
                  <TableHead className="text-xs font-semibold w-[84px] px-2">Pmt Date</TableHead>
                  <TableHead className="text-xs font-semibold w-[44px] text-center px-1">Inv</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 12 }).map((_, i) => (
                    <TableRow key={i}>{Array.from({ length: 11 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-16 text-muted-foreground">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      No transactions found. Adjust filters or ensure PFs are approved/paid.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, i) => {
                    const v = r.variance_amount || 0;
                    const isPositive = v > 0.005;
                    const isNegative = v < -0.005;
                    return (
                      <TableRow key={r.transaction_id || i} className="hover:bg-muted/20" data-testid={`vr-row-${i}`}>
                        <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <div className="flex items-center gap-1">
                              {r.bill_type === 'common_area' ? (
                                <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200 h-4 px-1 font-semibold">CA</Badge>
                              ) : r.bill_type === 'unit' ? (
                                <Badge variant="outline" className="text-[9px] bg-teal-50 text-teal-700 border-teal-200 h-4 px-1 font-semibold">Unit</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200 h-4 px-1">{'\u2014'}</Badge>
                              )}
                              {r.unit_number && (
                                <span className="text-[10px] font-mono text-muted-foreground">#{r.unit_number}</span>
                              )}
                            </div>
                            <span className="text-xs font-medium truncate" title={r.property_name || r.service_address}>
                              {r.property_name || '\u2014'}
                            </span>
                            {r.service_address && (
                              <span className="text-[10px] text-muted-foreground truncate" title={r.service_address}>{r.service_address}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono text-xs truncate" title={r.account_number}>{r.account_number || '\u2014'}</span>
                            {r.pf_batch_name && (
                              <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 h-4 px-1.5 font-semibold w-fit">{r.pf_batch_name}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs truncate" title={r.vendor_name}>{r.vendor_name || '\u2014'}</TableCell>
                        <TableCell className="text-xs font-mono text-right">{fmtC(r.approved_amount)}</TableCell>
                        <TableCell className="text-xs font-mono text-right font-semibold text-emerald-700">{fmtC(r.paid_amount)}</TableCell>
                        <TableCell className="text-xs font-mono text-right text-amber-700">{fmtC(r.transaction_fee)}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className="text-[10px] font-medium">{methodLabel(r.payment_method)}</Badge>
                        </TableCell>
                        <TableCell className={`text-xs font-mono text-right font-bold ${isPositive ? 'bg-red-50/60 text-red-700' : isNegative ? 'bg-blue-50/60 text-blue-700' : 'bg-sky-50/30 text-slate-600'}`}>
                          {isPositive && <TrendingDown className="h-3 w-3 inline mr-0.5 -mt-0.5" />}
                          {isNegative && <TrendingUp className="h-3 w-3 inline mr-0.5 -mt-0.5" />}
                          {fmtC(v)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.payment_date)}</TableCell>
                        <TableCell className="text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={() => openInvoice(r)}
                                data-testid={`vr-invoice-btn-${i}`}
                                aria-label="Open invoice"
                              >
                                <FileText className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">Open invoice</TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Load more */}
          {meta.has_more && (
            <div className="flex justify-center py-2">
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="vr-load-more">
                {loadingMore ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Loading...</> :
                  <><ChevronsDown className="h-3.5 w-3.5 mr-1" /> Load more ({(meta.total - rows.length).toLocaleString()} left)</>}
              </Button>
            </div>
          )}
        </div>

        {/* Invoice Drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="w-full sm:max-w-[720px] flex flex-col p-0" data-testid="vr-invoice-drawer">
            <SheetHeader className="px-5 py-3 border-b flex-row items-center justify-between">
              <div>
                <SheetTitle className="text-sm">Invoice {'\u00b7'} {drawerRow?.invoice_number || '\u2014'}</SheetTitle>
                <p className="text-[11px] text-muted-foreground">
                  {drawerRow?.vendor_name || ''} {'\u00b7'} {drawerRow?.account_number || ''} {'\u00b7'} {drawerRow?.pf_batch_name || ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDrawerOpen(false)}><X className="h-4 w-4" /></Button>
            </SheetHeader>
            <div className="flex-1 overflow-auto bg-muted/10 flex items-center justify-center">
              {drawerLoading ? (
                <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading invoice...</div>
              ) : pdfDataUrl ? (
                <iframe title="Invoice PDF" src={pdfDataUrl} className="w-full h-full border-0" data-testid="vr-invoice-pdf" />
              ) : (
                <div className="text-xs text-muted-foreground text-center px-4">
                  No invoice PDF available for this transaction.
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    </MainLayout>
  );
}

VarianceReport.displayName = 'VarianceReport';
